<script setup lang="ts">
import {ref, computed, inject, onMounted, onUnmounted, provide, watch} from 'vue';
import {QDialog, Screen} from 'quasar';
import {storeToRefs} from 'pinia';
import NamaCommon from '@/utils/common/NamaCommon.ts';
import NamaURLS from '@/utils/constants/NamaURLS.ts';
import Translator from '@/utils/common/Translator.ts';
import {useCrossFilterStore} from '@/stores/crossFilterStore.ts';
import {useNamaFlagsStore} from '@/stores/nama-flags-store.ts';
import CrossFilterRenderer from '@/components/bi/CrossFilterRenderer.ts';
import DashboardPresenter from '@/utils/presenters/DashboardPresenter.ts';
import {dashboardPresenterKey} from '@/utils/presenters/interfaces.ts';
import {
  getDashboardWidgetComponent,
  getDashboardWidgetDefinition,
  isAutoHeightWidget,
  sortChartLines,
  widgetGridArea
} from '@/components/dashboardWidgets/dashboardWidgetRegistry.ts';
import type {
  BIDrillDownClick,
  BIDrillDownByClick,
  BIDrillDownByPointValues,
  BIDrillDownFilterMetadata,
  BIDrillDownTarget,
  CrossFilterEmission
} from '@/components/bi/biChartTypes.ts';
import FieldTypesUtils from '@/utils/common/FieldTypesUtils.ts';
import FieldType from '@/utils/constants/FieldType.ts';
import DTOBIChartLine = namaTypes.DTOBIChartLine;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import DTOUserDashbordChartLine = namaTypes.DTOUserDashbordChartLine;
import QuestionField = namaTypes.QuestionField;

const {darkMode} = storeToRefs(useNamaFlagsStore());

interface DrillDownStep {
  mode: 'widgetDrillDown' | 'dimensionDrillBy' | 'dashboardDrillDown';
  // 6.2 widget drill-down fields
  sourceWidgetCode: string;
  targetWidgetId: string;
  targetWidgetCode: string;
  drillDownFilters: DTOFilledQuestionField[];
  filterMetadata: Record<string, BIDrillDownFilterMetadata>;
  targetLabel: string;
  sourceLabel?: string;
  // 6.3 dimension drill-by fields
  drillDownByTargetDimension?: string;
  drillDownByStack?: DTOFilledQuestionField[];
  drillByFilterLabel?: string;
  // dashboard drill-down fields
  targetDashboardId?: string;
}

const props = defineProps<{
  modelValue: boolean;
  dashboardId?: string;
  rootClick?: BIDrillDownClick;
  rootDrillByClick?: BIDrillDownByClick;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>();

const dialogModel = computed<boolean>({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

// The popup owns its presenter so the page's dashboard data and timers are never touched; widgets inside the
// popup inject it for their refresh / runtime-slot actions, which then carry the drill-down context.
const pagePresenter = inject(dashboardPresenterKey, undefined);
const popupPresenter = new DashboardPresenter();
provide(dashboardPresenterKey, popupPresenter);
// The page's auto-refresh timers read the shared cross-filter store; pause them while the popup owns it.
if (pagePresenter) pagePresenter.autoRefreshPaused = true;
let crossFilterEmittedInPopup = false;

const crossFilterStore = useCrossFilterStore();
const crossFilterSnapshot = snapshotCrossFilterStore();

const drillStack = ref<DrillDownStep[]>([]);
const currentChartLine = ref<DTOBIChartLine | undefined>();
const loading = ref<boolean>(false);
const errorMessage = ref<string | undefined>();
const chartKey = ref<number>(0);
const maximized = ref<boolean>(false);
const currentStep = computed(() => drillStack.value[drillStack.value.length - 1]);
const isDashboardMode = computed(() => currentStep.value?.mode === 'dashboardDrillDown');
const dashboardChartLines = computed<DTOUserDashbordChartLine[]>(() =>
  isDashboardMode.value ? sortChartLines(popupPresenter.fetchedData.value?.chartLines) : []);

function toggleMaximize() {
  maximized.value = !maximized.value;
}

onMounted(async () => {
  if (props.rootClick) {
    drillStack.value = [buildStepFromClick(props.rootClick)];
  } else if (props.rootDrillByClick) {
    drillStack.value = [buildStepFromDrillByClick(props.rootDrillByClick)];
  }
  await fetchCurrent();
});

onUnmounted(() => {
  popupPresenter.displayDashboard.value = false;
  popupPresenter.clearDrillDownContext();
  restoreCrossFilterStore();
  resumePageDashboard();
});

// Paused timers do not reschedule themselves, so a page with auto-refresh (or one whose data was fetched
// under the popup's filters) is refetched once; every other page is left untouched.
function resumePageDashboard() {
  if (!pagePresenter) return;
  pagePresenter.autoRefreshPaused = false;
  const data = pagePresenter.fetchedData.value;
  const hasAutoRefresh = (data?.refreshPer ?? 0) > 0 || (data?.chartLines ?? []).some(l => (l.refreshPer ?? 0) > 0);
  if (crossFilterEmittedInPopup || hasAutoRefresh)
    void pagePresenter.redrawWidgets('');
}

// Widget refreshes inside the popup go through popupPresenter.redrawWidgets and land in its fetchedData;
// in single-widget mode mirror the refreshed line back into the displayed chart line.
// Drill-by results are not reproducible through FetchBICharts, so a refresh in that mode is not mirrored.
watch(() => popupPresenter.fetchedData.value, (data) => {
  const widgetId = currentChartLine.value?.widgetId;
  if (isDashboardMode.value || currentStep.value?.mode === 'dimensionDrillBy' || !widgetId) return;
  const refreshed = data?.chartLines?.find(l => l.widgetId === widgetId);
  if (refreshed) {
    currentChartLine.value = refreshed as DTOBIChartLine;
    chartKey.value++;
  }
});

function snapshotCrossFilterStore() {
  return {
    questionFields: [...crossFilterStore.questionFields],
    filledQuestions: new Map(crossFilterStore.filledQuestions),
    localFilledQuestions: new Map(Array.from(crossFilterStore.localFilledQuestions, ([widgetId, filters]) => [widgetId, new Map(filters)])),
    widgetHighlights: new Map(crossFilterStore.widgetHighlights),
    widgetSlotOverrides: new Map(crossFilterStore.widgetSlotOverrides),
  };
}

// The cross-filter store is a singleton shared with the page; filters clicked inside the popup must not
// leak into the page once the popup closes.
function restoreCrossFilterStore() {
  crossFilterStore.questionFields = crossFilterSnapshot.questionFields;
  crossFilterStore.filledQuestions = crossFilterSnapshot.filledQuestions;
  crossFilterStore.localFilledQuestions = crossFilterSnapshot.localFilledQuestions;
  crossFilterStore.widgetHighlights = crossFilterSnapshot.widgetHighlights;
  crossFilterStore.widgetSlotOverrides = crossFilterSnapshot.widgetSlotOverrides;
}

function buildStepFromClick(click: BIDrillDownClick): DrillDownStep {
  if (click.target.targetType === 'dashboard') {
    return {
      mode: 'dashboardDrillDown',
      sourceWidgetCode: click.sourceWidgetCode,
      targetWidgetId: '',
      targetWidgetCode: '',
      targetDashboardId: click.target.targetDashboardId,
      drillDownFilters: click.drillDownFilters.map(f => ({id: f.id, value: f.value, sourceChart: f.sourceChart})),
      filterMetadata: click.filterMetadata,
      targetLabel: drillDownTitle(click.target),
      sourceLabel: click.sourceLabel,
    };
  }
  return {
    mode: 'widgetDrillDown',
    sourceWidgetCode: click.sourceWidgetCode,
    targetWidgetId: click.target.targetWidgetId ?? '',
    targetWidgetCode: click.target.targetWidgetCode ?? '',
    drillDownFilters: click.drillDownFilters.map(f => ({id: f.id, value: f.value})),
    filterMetadata: click.filterMetadata,
    targetLabel: drillDownTitle(click.target),
    sourceLabel: click.sourceLabel,
  };
}

function serializeDrillByValue(paramType: string, pointValues: BIDrillDownByPointValues | undefined): string {
  if (!pointValues) return '';
  if ((paramType === FieldType.Reference || paramType === FieldType.Genericreference) && pointValues.id) {
    return FieldTypesUtils.toString(paramType, {
      id: pointValues.id,
      entityType: pointValues.entityType ?? '',
      code: pointValues.code ?? '',
      actualCode: '',
      name1: pointValues.name1 ?? '',
      name2: pointValues.name2 ?? '',
    });
  }
  return pointValues.value ?? '';
}

function buildDrillByStackFromClick(click: BIDrillDownByClick, previousStack?: DTOFilledQuestionField[]): DTOFilledQuestionField[] {
  const stack: DTOFilledQuestionField[] = previousStack ? [...previousStack] : [];
  if (click.currentDimensionFieldId && click.currentDimensionValue) {
    const serialized = serializeDrillByValue(click.currentDimensionParamType, click.currentDimensionValue);
    stack.push({id: click.currentDimensionFieldId, value: serialized, sourceChart: click.sourceWidgetId});
  }
  return stack;
}

function renderDrillByFilterLabel(click: BIDrillDownByClick): string | undefined {
  const val = click.currentDimensionValue;
  if (!val) return undefined;
  const isRef = click.currentDimensionParamType === FieldType.Reference
    || click.currentDimensionParamType === FieldType.Genericreference;
  if (isRef) {
    const name = Translator.isArabic() ? (val.name1 || val.name2) : (val.name2 || val.name1);
    return name || val.code || '';
  }
  return val.value ?? '';
}

function buildStepFromDrillByClick(click: BIDrillDownByClick, previousStack?: DTOFilledQuestionField[]): DrillDownStep {
  return {
    mode: 'dimensionDrillBy',
    sourceWidgetCode: click.sourceWidgetCode,
    targetWidgetId: click.sourceWidgetId,
    targetWidgetCode: click.sourceWidgetCode,
    drillDownFilters: [],
    filterMetadata: {},
    targetLabel: click.dimensionLabel,
    drillDownByTargetDimension: click.targetDimensionFieldId,
    drillDownByStack: buildDrillByStackFromClick(click, previousStack),
    drillByFilterLabel: renderDrillByFilterLabel(click),
  };
}

async function fetchCurrent() {
  const step = currentStep.value;
  if (!step) return;
  loading.value = true;
  errorMessage.value = undefined;
  currentChartLine.value = undefined;
  try {
    if (step.mode === 'dashboardDrillDown')
      await fetchTargetDashboard(step);
    else
      await fetchSingleWidget(step);
  } catch (e) {
    errorMessage.value = (e as Error)?.message || Translator.translate('biFailedToLoadDrillDown');
  } finally {
    loading.value = false;
  }
}

async function fetchTargetDashboard(step: DrillDownStep) {
  maximized.value = true;
  popupPresenter.setDrillDownContext(step.sourceWidgetCode, step.drillDownFilters);
  // Keeps a target with report question fields on the prefilled path instead of opening its parameters dialog.
  popupPresenter.prefilledQuestions = step.drillDownFilters;
  await popupPresenter.fetchCharts(step.targetDashboardId);
  const targetCrossFilterFields = popupPresenter.fetchedData.value?.crossFilterQuestionFields;
  if (targetCrossFilterFields?.length)
    crossFilterStore.init(targetCrossFilterFields);
  chartKey.value++;
}

async function fetchSingleWidget(step: DrillDownStep) {
  popupPresenter.displayDashboard.value = false;
  popupPresenter.dashboardId = props.dashboardId;
  if (step.mode === 'widgetDrillDown')
    popupPresenter.setDrillDownContext(step.sourceWidgetCode, step.drillDownFilters);
  else
    popupPresenter.clearDrillDownContext();

  const requestData: Record<string, unknown> = {
    id: props.dashboardId || '',
    widgetId: step.targetWidgetId,
    activeCrossFilters: crossFilterStore.getFilledQuestionFields(),
  };
  if (step.mode === 'widgetDrillDown') {
    requestData.drillDownSourceWidgetCode = step.sourceWidgetCode;
    requestData.drillDownFilters = step.drillDownFilters;
  } else if (step.mode === 'dimensionDrillBy') {
    requestData.drillDownByTargetDimension = step.drillDownByTargetDimension;
    requestData.drillDownByStack = step.drillDownByStack ?? [];
  }

  const response = await NamaCommon.namaFetch(NamaURLS.FetchSingleBIWidget, {data: requestData});
  if (response?.result?.succeded) {
    currentChartLine.value = response.data as DTOBIChartLine;
    chartKey.value++;
  } else {
    errorMessage.value = Translator.translate('biFailedToLoadDrillDown');
  }
}

function drillDownTitle(target: BIDrillDownTarget): string {
  const isArabic = Translator.isArabic();
  return (isArabic ? target.arTitle : target.enTitle) || target.enTitle || target.arTitle || '';
}

function currentTitle(): string {
  if (isDashboardMode.value)
    return currentStep.value?.targetLabel ?? '';
  if (!currentChartLine.value) return '';
  return Translator.isArabic()
    ? (currentChartLine.value.arChartTitle || currentChartLine.value.enChartTitle || '')
    : (currentChartLine.value.enChartTitle || currentChartLine.value.arChartTitle || '');
}

function buildBreadcrumbLabel(step: DrillDownStep): string {
  if (step.mode === 'dimensionDrillBy') {
    if (step.drillByFilterLabel)
      return step.drillByFilterLabel + ' → ' + step.targetLabel;
    return step.targetLabel;
  }
  const parts: string[] = [];
  for (const filter of step.drillDownFilters) {
    const metadata = step.filterMetadata[filter.id ?? ''];
    if (!metadata) continue;
    const filterTitle = Translator.isArabic() ? metadata.arTitle : metadata.enTitle;
    const questionField = {
      id: filter.id,
      fieldType: metadata.paramType,
      metaData: {
        enumTypeName: metadata.enumTypeName,
        referencedEntityType: metadata.referencedEntityType,
      }
    } as QuestionField;
    const rendered = CrossFilterRenderer.renderValue(filter, questionField);
    if (rendered)
      parts.push(filterTitle ? `${filterTitle}: ${rendered}` : rendered);
    else if (filterTitle)
      parts.push(filterTitle);
  }
  if (parts.length)
    return parts.join(' | ');
  return step.targetLabel;
}

async function onNestedDrillDown(click: BIDrillDownClick) {
  drillStack.value.push(buildStepFromClick(click));
  await fetchCurrent();
}

async function onNestedDrillDownBy(click: BIDrillDownByClick) {
  const previousStack = currentStep.value?.mode === 'dimensionDrillBy' ? currentStep.value.drillDownByStack : undefined;
  drillStack.value.push(buildStepFromDrillByClick(click, previousStack));
  await fetchCurrent();
}

async function onCrossFilterClicked(emissions: CrossFilterEmission[]) {
  const sourceChartId = emissions?.[0]?.sourceChartId ?? '';
  crossFilterEmittedInPopup = true;
  crossFilterStore.emitFromChart(sourceChartId, emissions);
  await refetchAfterCrossFilterChange();
}

async function refetchAfterCrossFilterChange() {
  crossFilterEmittedInPopup = true;
  if (isDashboardMode.value) {
    await popupPresenter.doFetchCharts(popupPresenter.dashboardId, true, false);
    chartKey.value++;
  } else {
    await fetchCurrent();
  }
}

async function goToBreadcrumb(index: number) {
  if (index >= drillStack.value.length - 1) return;
  drillStack.value = drillStack.value.slice(0, index + 1);
  await fetchCurrent();
}

const dashboardGridStyle = computed<Record<string, string>>(() => {
  if (Screen.lt.sm) return {};
  const data = popupPresenter.fetchedData.value;
  const colsCount = data?.colsCount || 1;
  const rowsCount = data?.rowsCount || 1;
  let maxBottomRow = 0;
  for (const line of dashboardChartLines.value) {
    const bottom = (line.rowNumber ?? 1) + (line.rows ?? 1) - 1;
    if (bottom > maxBottomRow) maxBottomRow = bottom;
  }
  // Every `rowsCount` rows fill one popup height; extra rows scroll, like paging on the dashboard page.
  return {
    gridTemplateColumns: 'repeat(' + colsCount + ', minmax(0, 1fr))',
    gridTemplateRows: 'repeat(' + Math.max(rowsCount, maxBottomRow) + ', ' + (100 / rowsCount) + '%)',
  };
});

function widgetCellStyle(line: DTOUserDashbordChartLine): Record<string, string | undefined> {
  if (Screen.lt.sm)
    return {width: '100%', minHeight: isAutoHeightWidget(line) ? undefined : '300px'};
  return {gridArea: widgetGridArea(line), minHeight: isAutoHeightWidget(line) ? undefined : '0'};
}

function crossFilterLegend(line: DTOUserDashbordChartLine): string | undefined {
  if (line.chartType !== 'CrossFilterControl' || line.crossFilterControlQuestionField?.hideTitle)
    return undefined;
  return Translator.translateTitledId(undefined, line.crossFilterControlQuestionField);
}
</script>

<template>
  <q-dialog v-model="dialogModel" :maximized="maximized">
    <q-card :dark="darkMode" class="drill-down-card"
            :class="[
              darkMode ? 'tw-bg-namaBackground-bright-dark tw-text-gray-100' : 'tw-bg-namaBackground-bright-light tw-text-gray-800',
              maximized ? 'drill-down-card--maximized' : 'drill-down-card--resizable'
            ]">
      <q-card-section class="tw-flex tw-items-center tw-gap-2 tw-border-b"
                      :class="darkMode ? 'tw-border-gray-600' : 'tw-border-gray-200'">
        <q-icon name="analytics" size="20px" class="tw-text-primary"/>
        <div class="tw-text-sm tw-font-semibold tw-grow">{{ currentTitle() }}</div>
        <q-btn flat round dense :icon="maximized ? 'fullscreen_exit' : 'fullscreen'" @click="toggleMaximize"/>
        <q-btn flat round dense icon="close" @click="dialogModel = false"/>
      </q-card-section>
      <q-card-section class="tw-py-2 tw-border-b"
                      :class="darkMode ? 'tw-border-gray-600 tw-bg-gray-800' : 'tw-border-gray-200 tw-bg-gray-50'">
        <q-breadcrumbs :dark="darkMode" class="tw-text-xs">
          <q-breadcrumbs-el v-for="(step, index) in drillStack"
                            :key="index"
                            :label="buildBreadcrumbLabel(step)"
                            :class="{'tw-cursor-pointer tw-text-primary': index < drillStack.length - 1}"
                            @click="goToBreadcrumb(index)"/>
        </q-breadcrumbs>
      </q-card-section>
      <q-card-section class="drill-down-body"
                      :class="[darkMode ? 'tw-bg-namaBackground-bright-dark' : 'tw-bg-namaBackground-bright-light',
                               isDashboardMode ? 'tw-overflow-auto' : 'tw-overflow-hidden']">
        <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-h-full">
          <q-spinner size="40px" color="primary"/>
        </div>
        <div v-else-if="errorMessage" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
          <q-icon name="warning" color="orange" size="2rem"/>
          <p class="tw-text-sm tw-mt-2" :class="darkMode ? 'tw-text-gray-400' : 'tw-text-gray-600'">{{ errorMessage }}</p>
        </div>
        <template v-else-if="isDashboardMode">
          <div v-if="dashboardChartLines.length"
               class="tw-h-full tw-w-full desktop:tw-grid mobile:tw-flex mobile:tw-flex-wrap"
               :style="dashboardGridStyle">
            <div v-for="line in dashboardChartLines" :key="line.widgetId"
                 class="tw-p-1 tw-max-w-full tw-overflow-hidden"
                 :style="widgetCellStyle(line)">
              <div v-if="line.chartType"
                   class="tw-p-0.5 tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-rounded tw-shadow tw-relative"
                   :class="isAutoHeightWidget(line) ? '' : 'tw-h-full'">
                <span v-if="crossFilterLegend(line)" class="cf-legend" v-text="crossFilterLegend(line)"/>
                <component :is="getDashboardWidgetComponent(line)"
                           :chartLine="line"
                           :isThreeD="getDashboardWidgetDefinition(line)?.isThreeD"
                           :isScheduler="getDashboardWidgetDefinition(line)?.isScheduler"
                           :dashboardId="popupPresenter.dashboardId"
                           :chartKey="String(chartKey + '-' + line.widgetId)"
                           @crossFilterClicked="onCrossFilterClicked"
                           @drillDownRequested="onNestedDrillDown"
                           @drillDownByRequested="onNestedDrillDownBy"
                           @filtersChanged="refetchAfterCrossFilterChange"/>
              </div>
            </div>
          </div>
          <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
            <q-icon name="dashboard" size="2rem" :class="darkMode ? 'tw-text-gray-500' : 'tw-text-gray-400'"/>
            <p class="tw-text-sm tw-mt-2" :class="darkMode ? 'tw-text-gray-400' : 'tw-text-gray-600'">
              {{ Translator.translate('biDrillDownNoWidgets') }}
            </p>
          </div>
        </template>
        <component v-else-if="currentChartLine"
                   :is="getDashboardWidgetComponent(currentChartLine)"
                   :chartLine="currentChartLine"
                   :isThreeD="getDashboardWidgetDefinition(currentChartLine)?.isThreeD"
                   :isScheduler="getDashboardWidgetDefinition(currentChartLine)?.isScheduler"
                   :dashboardId="dashboardId"
                   :chartKey="String(chartKey)"
                   @crossFilterClicked="onCrossFilterClicked"
                   @drillDownRequested="onNestedDrillDown"
                   @drillDownByRequested="onNestedDrillDownBy"
                   @filtersChanged="refetchAfterCrossFilterChange"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.drill-down-card {
  display: flex;
  flex-direction: column;
}

.drill-down-card--resizable {
  width: 90vw;
  max-width: 95vw;
  height: 80vh;
  max-height: 95vh;
  resize: both;
  overflow: auto;
}

.drill-down-card--maximized {
  width: 100%;
  height: 100%;
}

.drill-down-body {
  flex: 1 1 auto;
  min-height: 0;
}

.cf-legend {
  @apply tw-absolute -tw-top-2 tw-left-3 tw-px-1.5 tw-text-sm tw-font-semibold tw-z-[1] tw-pointer-events-none tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-max-w-[calc(100%_-_24px)] tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark;
}
</style>
